import React from "react";

const CHIP_INFO = [
  { key: "summary_signal", label: "Summary" },
  { key: "reference", label: "Reference" },
  { key: "details", label: "Details" },
  { key: "source", label: "Source" },
  { key: "executable", label: "Executable" },
];

// Convert summary_signal values (1, 2, 3) to symbol equivalents (!, ✕, ✓)
const convertSignalToStatus = (signal) => {
  switch (String(signal)) {
    case "1":
      return "✕";
    case "2":
      return "!";
    case "3":
      return "✓";
    default:
      return "-";
  }
};

const getChipColor = (status) => {
  switch (status) {
    case "✓":
      return "text-green-600";
    case "✕":
      return "text-red-600";
    case "!":
      return "text-yellow-500";
    default:
      return "text-gray-400";
  }
};

const getRectangleColor = (status) => {
  switch (status) {
    case "✓":
      return "bg-green-600";
    case "✕":
      return "bg-red-600";
    case "!":
      return "bg-yellow-500";
    default:
      return "bg-gray-400";
  }
};

const getRectangleCount = (status) => {
  switch (status) {
    case "✓":
      return 3;
    case "!":
      return 2;
    case "✕":
      return 1;
    default:
      return 0;
  }
};

const getSummaryRectangles = (status) => {
  const count = getRectangleCount(status);
  const color = getRectangleColor(status);
  
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3].map((index) => (
        <div
          key={index}
          className={`w-2.5 h-2.5 border border-gray-400 ${index <= count ? color : "bg-white"}`}
        />
      ))}
    </div>
  );
};

const getChipTooltip = (label, status) => {
    switch (label) {
    case "Summary":
    case "Details":
        switch (status) {
          case "✓":
            return ` ✓ Complete Data \n${getLabelDescription(label)}`;
          case "✕":
            return ` ✕ Missing / Invalid value \n${getLabelDescription(label)}`;
          case "!":
            return ` ! Partially complete data \n${getLabelDescription(label)}`;
          default:
            return ` Unknown `;
        }
    case "Reference":
    case "Source":
    case "Executable":
        switch (status) {
          case "✓":
            return ` ✓ Working link(s) \n${getLabelDescription(label)}`;
          case "✕":
            return ` ✕ Broken link(s) \n${getLabelDescription(label)}`;
          case "!":
            return ` ! Few working links \n${getLabelDescription(label)}`;
          default:
            return ` Unknown `;
        }
  }
};

const getLabelDescription = (label) => {
  switch (label) {
    case "Summary":
      return ` ${label}: A brief overview of the tool's functionality. `;
    case "Reference":
      return ` ${label}: Link(s) to official publications. `;
    case "Details":
      return ` ${label}: Information about the tool's planning language, its class, and functionality. `;
    case "Source":
      return ` ${label}: Link to the tool's source code repository. `;
    case "Executable":
      return ` ${label}: Link to the tool's executable file. `;
    default:
      return ` ${label}: No description available. `;
  }
};

export default function ChipRow({ chipStatus }) {
  return (
    <div className="flex flex-row gap-3 mt-2 flex-wrap">
      {CHIP_INFO.map(({ key, label }) => {
        // For summary field, convert signal to status symbol
        const displayStatus = key === "summary_signal" 
          ? convertSignalToStatus(chipStatus?.[key])
          : (chipStatus?.[key] || "-");
        
        return (
          <div key={key} className="flex items-center gap-1 whitespace-nowrap">
            {label === "Summary" ? (
              <div title={getChipTooltip(label, displayStatus)}>
                {getSummaryRectangles(displayStatus)}
              </div>
            ) : (
              <span className={`text-lg font-bold ${getChipColor(displayStatus)}`} title={getChipTooltip(label, displayStatus)}>{displayStatus}</span>
            )}
            <span className="text-sm text-gray-700" title={getChipTooltip(label, displayStatus)}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}
